import type { SubmitHandler } from 'react-hook-form';
import { faBuilding } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiGetOrganizationResponseBody } from '~/lib/api/modules/Organizations';
import type { IUseUpdateOrganizationMutationValues } from '~/lib/api/modules/Organizations/hooks/useUpdateOrganizationMutation';
import type { IOrganizationFormValues } from './OrganizationForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useOrganizationQuery } from '~/lib/api/modules/Organizations/hooks/useOrganizationQuery';
import { useUpdateOrganizationMutation } from '~/lib/api/modules/Organizations/hooks/useUpdateOrganizationMutation';
import { OrganizationForm } from './OrganizationForm';

export type IUpdateOrganizationFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiGetOrganizationResponseBody,
    inputValues: IUseUpdateOrganizationMutationValues,
  ) => void;
  cancelUrl?: string;
  organizationId: string;
};

export const UpdateOrganizationForm: React.FC<IUpdateOrganizationFormProps> = (
  props,
) => {
  const { sx, onSuccess, cancelUrl, organizationId } = props;
  const { t } = useTranslation();
  const organizationQuery = useOrganizationQuery({
    organizationId,
  });
  const updateOrganizationMutation = useUpdateOrganizationMutation();
  const organization = organizationQuery.data;

  const handleSubmit: SubmitHandler<IOrganizationFormValues> = (values) => {
    if (updateOrganizationMutation.isLoading) {
      return;
    }

    return updateOrganizationMutation.mutateAsync(
      {
        organizationId,
        body: {
          name: values.name,
          email: values.email,
          appleCompanyName: values.name,
          ...(values.appleSetupDone
            ? undefined
            : {
                appleTeamId: values.appleTeamId,
              }),
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title icon={<FontAwesomeIcon icon={faBuilding} />}>
        {t('UpdateOrganizationForm.title')}
      </ContentBlock.Title>

      <ContentBlock.Suspense
        loading={organizationQuery.isLoading}
        error={organizationQuery.error}
      >
        {organization ? (
          <OrganizationForm
            mode="edition"
            cancelUrl={cancelUrl}
            defaultValues={{
              name: organization.name,
              email: organization.email,
              appleSetupDone: organization.apple.setupDone,
              appleTeamStatus: organization.apple.teamStatus,
              appleTeamId: organization.apple.teamId,
            }}
            onSubmit={handleSubmit}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
